import { appUrl } from '@/settings'
import session from 'session-plugin/lib/utils/webapi/session'

let Cookies = null
try {
 Cookies = require('js-cookie')
} catch (e) {}

const defaultOptions = {
  loginRouteName: 'Login',
  loginPath: `${appUrl}login`
}

const defaultValues = {
  st: null,
  cookie: {},
  userRole: null,
  _id: null,
  userID: {},
  username: '',
  ip: '',
  error: '',
  systemMode: '',
  refreshed: false
}

class Session {
  constructor ({ router, options = {}, timeoutWarning = 60000 } = {}) {
    this.options = {...defaultOptions, ...options}
    this.timeoutWarning = timeoutWarning
    this.beforeExpire = []
    this.expired = []
    this.$router = router || null
    Object.assign(this, defaultValues)
    this.refreshed = true
    this.create()
    this.ready = this.readySet()
    window.$s = this
  }

  async checkSessionValidity () {
    const resp = await session.createUrl()
    const jData = await resp.json()
    if (resp.ok) {
      return true
    } else {
      this.error = jData.error
      return false
    }
  }

  async check (allowedUsers, userDiscriminator = 'userRole') {
    try {
      await this.ready
      const userKey = this[userDiscriminator]
      if (Array.isArray(userKey)) {
        return userKey.some((x) => {
          return allowedUsers.includes(x)
        })
      } else {
        return allowedUsers.includes(userKey)
      }
    } catch {
      return false
    }
  }

  async logout () {
    clearTimeout(this.st)
    clearTimeout(this.wt)
    Object.assign(this, defaultValues)
    await session.logoutUrl()
    Cookies && Cookies.remove('key')
    await this.create()
  }

  async destroySession ({ message } = {}) {
    await this.logout()
    if (window.location.pathname !== this.options.loginPath) {
      if (this.options.loginRouteName) {
        this.$router.push({ name: this.options.loginRouteName, params: { message } })
      } else {
        this.$router.push({ path: this.options.loginPath })
      }
    }
  }

  async login (creds, forceful) {
    await session.logoutUrl()
    this.ready = this.readySet()
    const resp = await session.loginUrl(forceful, creds)
    const jData = await resp.json()
    if (resp.ok) {
      Object.assign(this, jData)
      this.$resolve()
    } else {
      this.error = jData.error
      await this.create()
      this.$resolve()
    }
    resp.jData = jData
    return resp
  }

  async loginSSO (sessionObject) {
    this.ready = this.readySet()
    if (sessionObject) {
      Object.assign(this, sessionObject)
      this.$resolve()
    } else {
      this.error = 'No session has been passed.'
      await this.create()
      this.$resolve()
    }
    return sessionObject
  }

  async loginWithOtp (creds, forceful) {
    this.ready = this.readySet()
    const resp = await session.loginUrl(forceful, creds)
    const jData = await resp.json()
    resp.jData = jData
    return resp
  }

  async resendOtp (username) {
    this.ready = this.readySet()
    const resp = await session.resendOtp(username)
    const jData = await resp.json()
    resp.jData = jData
    return resp
  }

  async verify (username, otp, forceful) {
    await session.logoutUrl()
    this.ready = this.readySet()
    const resp = await session.verifyOtp(username, otp, forceful)
    const jData = await resp.json()
    if (resp.ok) {
      Object.assign(this, jData)
      this.$resolve()
    } else {
      this.error = jData.error
      await this.create()
      this.$resolve()
    }
    resp.jData = jData
    return resp
  }

  async backupCodeLogin (username, backupCode, forceful) {
    await session.logoutUrl()
    this.ready = this.readySet()
    const resp = await session.backupCodeLogin(username, backupCode, forceful)
    const jData = await resp.json()
    if (resp.ok) {
      Object.assign(this, jData)
      this.$resolve()
    } else {
      this.error = jData.error
      await this.create()
      this.$resolve()
    }
    resp.jData = jData
    return resp
  }

  async create () {
    this.ready = this.readySet()
    const resp = await session.createUrl()
    const jData = await resp.json()
    if (resp.ok) {
      Object.assign(this, jData)
      this.$resolve()
    }
    return resp
  }

  async setBeforeExpiry () {
    await this.ready
    clearTimeout(this.wt)
    if (this.userRole.includes('guest')) return false
    this.wt = setTimeout(() => {
      this.refreshed = false
      this.beforeExpire.forEach((fx) => fx())
      this.setExpiry()
    }, this.cookie.originalMaxAge - this.timeoutWarning)
  }

  async setExpiry () {
    this.st = setTimeout(() => {
      this.destroySession({
        message: 'You have been automatically logged out because you were idle for too long.'
      })
    }, this.timeoutWarning)
  }
  async refresh (create = true) {
    this.refreshed ? (create = false) : this.refreshed = true;
    clearTimeout(this.st)
    create && await this.create()
    await this.setBeforeExpiry()
  }

  static exists () {
    return document.cookie
  }

  readySet () {
    return new Promise((resolve, reject) => {
      this.$resolve = resolve
      this.$reject = reject
    })
  }

  async guard (vm, next) {
    await this.ready
    if (!vm.$options.session) {
      next && next()
    } else {
      if (vm.$options.session.allow || vm.$options.session.restrict) {
        let allowed = true
        let restricted = false
        if (vm.$options.session.allow) allowed = await this.check(vm.$options.session.allow, vm.$options.session.userDiscriminator)
        if (vm.$options.session.restrict) restricted = await this.check(vm.$options.session.restrict, vm.$options.session.userDiscriminator)
        if (restricted) {
          this.destroySession()
        } else if (!restricted && !allowed) {
          this.destroySession()
        } else if (next) {
          const isValid = await this.checkSessionValidity()
          if (isValid) {
            next()
          }
          else {
            this.destroySession()
          }
        }
      }
    }
  }
}

export default Session
